import React, { FC, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  ListItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { useUnit } from 'effector-react';
import { agencyDictionary, clientDictionary } from '../dictionaries/model';

type DictionaryModel = typeof agencyDictionary | typeof clientDictionary;

type DictionaryListboxProps = React.HTMLAttributes<HTMLUListElement> & {
  isValueInvalid?: boolean;
  onAddToDictionary?: () => void;
};

const DictionaryListbox = React.memo(
  React.forwardRef<HTMLUListElement, DictionaryListboxProps>(
    ({ isValueInvalid, onAddToDictionary, ...ulProps }, ref) => (
      <>
        {isValueInvalid && onAddToDictionary && (
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              backgroundColor: 'background.paper',
              borderBottom: 1,
              borderColor: 'divider',
              p: 0.5,
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Button
              size="small"
              fullWidth
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onAddToDictionary?.();
              }}
            >
                Добавить в глобальный словарь
              </Button>
            </Box>
          )}
          <Box component="ul" {...ulProps} ref={ref} />
        </>
      ),
  ),
);



type DictionaryOnChangeMeta = {
  skipAutosave?: boolean;
};

type Props = {
  label: string;
  value: string;
  onChange: (value: string, meta?: DictionaryOnChangeMeta) => void;
  dictionaryModel: DictionaryModel;
  onValidityChange?: (valid: boolean) => void;
  onSaveAfterAdd?: () => void;
  disabled?: boolean;
};

const DELETE_CONFIRM_TEXT =
  'Вы уверены, что хотите удалить значение из словаря во всех карточках?';

export const DictionaryAutocomplete: FC<Props> = ({
  label,
  value,
  onChange,
  dictionaryModel,
  onValidityChange,
  onSaveAfterAdd,
  disabled,
}) => {
  const { $meta, $words, loadMoreRequested, searchChanged, deleteWordFx, addWordDone } =
    dictionaryModel;

  const [meta, words, knownWords, knownWordsOnlyDictionary] = useUnit([
    $meta,
    $words,
    dictionaryModel.$knownWords,
    dictionaryModel.$knownWordsOnlyDictionary,
  ]);

  const [deleteCandidate, setDeleteCandidate] = useState<string | null>(null);
  const listboxRef = useRef<HTMLUListElement | null>(null);
  const lastScrollTopRef = useRef<number | null>(null);
  const prevOptionsLengthRef = useRef<number>(0);
  const pendingSaveAfterAddRef = useRef<string | null>(null);
  const lastValidValueRef = useRef<string>('');

  const handleInputChange = useCallback(
    (_: unknown, newValue: string, reason: string) => {
      onChange(newValue);

      if (reason === 'input') {
        searchChanged(newValue.trim());
      }

      if (reason === 'clear') {
        searchChanged('');
      }
    },
    [onChange, searchChanged],
  );

  const handleChange = useCallback(
    (_: unknown, newValue: string | null) => {
      if (typeof newValue === 'string') {
        onChange(newValue);
      }
    },
    [onChange],
  );

  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent, opts: string[]) => {
      if (event.key !== 'Enter' || opts.length === 0) return;
      const input = event.currentTarget as HTMLInputElement;
      const activeId = input.getAttribute?.('aria-activedescendant');
      if (!activeId) {
        event.preventDefault();
        onChange(opts[0]);
        input.blur();
      }
    },
    [onChange],
  );

  const handleListboxScroll = useCallback(
    (event: React.SyntheticEvent) => {
      const target = event.currentTarget as HTMLUListElement;
      listboxRef.current = target;
      lastScrollTopRef.current = target.scrollTop;
      if (target.scrollTop + target.clientHeight >= target.scrollHeight - 16) {
        loadMoreRequested();
      }
    },
    [loadMoreRequested],
  );

  const handleDeleteClick = useCallback((event: React.MouseEvent, option: string) => {
    event.stopPropagation();
    setDeleteCandidate(option);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDeleteCandidate(null);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!deleteCandidate || !meta.uuid) {
      setDeleteCandidate(null);
      return;
    }

    deleteWordFx({
      uuid: meta.uuid,
      word: deleteCandidate,
    });
    setDeleteCandidate(null);
  }, [deleteCandidate, deleteWordFx, meta.uuid]);

  const handleAddToDictionary = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed) return;
    pendingSaveAfterAddRef.current = trimmed;
    dictionaryModel.addWord(trimmed);
  }, [value, dictionaryModel]);

  const handleCancelInvalid = useCallback(() => {
    onChange(lastValidValueRef.current, { skipAutosave: true });
    searchChanged('');
  }, [onChange]);


  const options = useMemo(() => words ?? [], [words]);

  const canSuggestAdd = useMemo(() => {
    const trimmed = value.trim();
    if (!trimmed) return false;
    const known = knownWords[trimmed];
    if (known === true) return false;
    if (known === false) return true;
    return true;
  }, [knownWords, value]);

  const canSuggestAddOnlyDictionary = useMemo(() => {
    const trimmed = value.trim();
    if (!trimmed) return false;
    const known = knownWordsOnlyDictionary[trimmed];
    if (known === true) return false;
    if (known === false) return true;
    return true;
  }, [knownWordsOnlyDictionary, value]);

  const isValueValidForSave = useMemo(() => {
    const trimmed = value.trim();
    if (trimmed === '') return true;
    return knownWords[trimmed] === true;
  }, [knownWords, value]);

  const isValueInvalid = useMemo(() => {
    const trimmed = value.trim();
    return trimmed !== '' && knownWords[trimmed] === false;
  }, [knownWords, value]);

  const trimmedValue = useMemo(() => value.trim(), [value]);

  React.useEffect(() => {
    onValidityChange?.(isValueValidForSave);
  }, [isValueValidForSave, onValidityChange]);

  React.useEffect(() => {
    if (isValueValidForSave) {
      lastValidValueRef.current = value;
    }
  }, [isValueValidForSave, value]);
  React.useEffect(() => {
    const unsubscribe = addWordDone.watch((payload) => {
      const expected = pendingSaveAfterAddRef.current;
      if (expected !== null && payload.word === expected) {
        pendingSaveAfterAddRef.current = null;
        onSaveAfterAdd?.();
      }
    });
    return () => unsubscribe();
  }, [addWordDone, onSaveAfterAdd]);

  React.useEffect(() => {
    if (!trimmedValue) return;
    if (!meta.uuid) return;
    const known = knownWords[trimmedValue];
    if (known !== undefined) return;
    dictionaryModel.checkWord(trimmedValue);
  }, [dictionaryModel, meta.uuid, trimmedValue]);

  useLayoutEffect(() => {
    const prevLength = prevOptionsLengthRef.current;
    const nextLength = options.length;
    prevOptionsLengthRef.current = nextLength;

    if (
      nextLength <= prevLength ||
      lastScrollTopRef.current === null ||
      lastScrollTopRef.current < 0
    ) {
      return;
    }

    const savedScrollTop = lastScrollTopRef.current;
    const listbox = listboxRef.current;
    if (!listbox) return;

    let cancelled = false;
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled && listboxRef.current) {
          listboxRef.current.scrollTop = savedScrollTop;
        }
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [options.length]);



  return (
    <>
      <Autocomplete
        autoHighlight
        openOnFocus
        blurOnSelect="touch"
        disableClearable={!value.trim()}
        sx={{
          '.MuiInputLabel-root': {
            zIndex: 99,
            pointerEvents: 'none',
          },
        }}
        slotProps={{
          paper: {
            sx: {
              '& .MuiAutocomplete-noOptions': {
                padding: 0.5,
              },
              '& .MuiAutocomplete-loading': {
                padding: 1,
                minHeight: '40px',
              },
            },
          },
        }}
        options={options}
        value={value}
        loading={meta.loading}
        filterOptions={(v) => v}
        noOptionsText={
          <Button
          size="small"
          fullWidth
          onClick={handleAddToDictionary}
        >
            Добавить в глобальный словарь
          </Button>
        }
        onInputChange={handleInputChange}
        onChange={handleChange}
        disabled={disabled}
        ListboxComponent={DictionaryListbox}

        ListboxProps={
          {
            role: 'listbox',
            onScroll: handleListboxScroll,
            isValueInvalid,
            onAddToDictionary: handleAddToDictionary,
          } as DictionaryListboxProps
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="standard"
            fullWidth
            error={isValueInvalid}
            helperText={isValueInvalid ? 'Значение не сохранено. Добавьте значение в словарь' : undefined}
            inputProps={{
              ...params.inputProps,
              onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                handleInputKeyDown(e, options);
                if (!e.defaultPrevented && params.inputProps?.onKeyDown) {
                  params.inputProps.onKeyDown(e);
                }
              },
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {meta.loading ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <ListItem
            {...props}
            key={`${option}_${props.accessKey}`}
            // secondaryAction={
            //   <IconButton
            //     edge="end"
            //     aria-label="delete"
            //     size="small"
            //     tabIndex={-1}
            //     onClick={(event) => handleDeleteClick(event, option)}
            //   >
            //     {/* <DeleteIcon fontSize="small" /> */}
            //   </IconButton>
            // }
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {option}
          </ListItem>
        )}
      />

      <Box sx={{ mt: 0.5, minHeight: 34, alignSelf: 'flex-start' }} gap={1} display="flex">
        {(canSuggestAdd||canSuggestAddOnlyDictionary) && (
         <> <Button
            size="small"
            onClick={handleAddToDictionary}
          >
            Добавить в глобальный словарь
          </Button>
          </>
        )}
          {canSuggestAdd && (
         <>  <Button
            size="small"
            onClick={handleCancelInvalid}
          >
            Отмена
          </Button>
          </>
        )}
      </Box>

      <Dialog open={Boolean(deleteCandidate)} onClose={handleCloseDialog}>
        <DialogTitle>Удаление значения из словаря</DialogTitle>
        <DialogContent>
          <DialogContentText>{DELETE_CONFIRM_TEXT}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Отмена
          </Button>
          <Button onClick={handleConfirmDelete} color="error">
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
